import { capitalize } from "@/tabs/Receptforskrivning/helpers";
import { NllKodverk } from "./nllModule";

export type SparrValue = {
    sparr_legal_ground: string;
    sparr_subject_type: string;
    extra?: string;
};

export type NllKodverkOption = {
    value: string | null;
    /**
     * Label som visas för utskrivaren
     */
    label: string | null;
    /**
     * Label som visas för patient
     */
    patient_display?: string | null;
};

export const NllLockTypeDescription = {
    integritet: {
        label: "Integritetsspärr",
        description: `
           En spärr som patienten själv sätter i Läkemedelskollen för att dölja läkemedelsuppgifter för vård- och 
           apotekspersonal. Patienten kan när som helst skapa och ta bort spärren via Läkemedelskollen.
        `,
    },
    sekretess: {
        label: "Sekretesspärr",
        description: `
            En spärr som förskrivaren sätter för att skydda känsliga uppgifter 
            från patienten själv eller dennes vårdnadshavare
        `,
    },
};

/**
 * Namnsättning med upper case snake case för att göra det lättare att hämta ut information i SparrForskrivning
 * och andra platser som ska visa information om spärrar
 */

export const NllLockType = {
    INTEGRITET: {
        code: "INTEGRITET",
        // Från NLL
        display: "Integritetsspärr",
        register_display: "Registrera integritetsspärr",
        deregister_display: "Avregistrera integritetsspärr",
    },
    SEKRETESS_PAT: {
        code: "SEKRETESS_PAT",
        // Från NLL
        display: "Sekretess-spärr mot patient",
        register_display: "Registrera sekretesspärr, behandlingsorsak",
        deregister_display: "Avregistrera sekretesspärr, behandlingsorsak",
    },
    SEKRETESS_VH: {
        code: "SEKRETESS_VH",
        // Från NLL
        display: "Sekretess-spärr mot vårdnadshavare",
        register_display: "Registrera sekretesspärr, vårdnadshavare",
        deregister_display: "Avregistrera sekretesspärr, vårdnadshavare",
    },
};

export const NllLockSubject = {
    BEHANDLINGSORSAK: {
        code: "BEHANDLINGSORSAK",
        // Från NLL
        display: "Behandlingsorsak spärrad",
        // Vår label
        label: "Sekretesspärr",
        description: `
            En spärr som förskrivaren sätter för att skydda känsliga uppgifter från patienten
            själv eller dennes vårdnadshavare.
        `,
    },
    FORSKRIVNINGSKEDJA: {
        code: "FORSKRIVNINGSKEDJA",
        display: "Förskrivningskedja spärrad",
    },
    UTTAGPAPPERSRECEPT: {
        code: "UTTAGPAPPERSRECEPT",
        display: "Uttag pappersrecept spärrad",
    },
};

export const NllLock = {
    [`${NllLockType.SEKRETESS_PAT.code}-${NllLockSubject.BEHANDLINGSORSAK.code}`]: {
        value: {
            sparr_legal_ground: NllLockType.SEKRETESS_PAT.code,
            sparr_subject_type: NllLockSubject.BEHANDLINGSORSAK.code,
        },
        label: "Spärr av behandlingsorsak för patient, sekretesspärr",
        description: "Du som förskrivare begär att E-hälsomyndigheten ska spärra behandlingsorsaken för patienten",
        disclaimer: `
            Du som förskrivare begär att E-hälsomyndigheten ska spärra behandlingsorsaken för patienten.
            Spärren döljer behandlingsorsak för patienten i NLL och anslutande system.
        `,
        infoNll: `
            Som förskrivare av läkemedel begär jag att E-hälsomyndigheten ska spärra behandlingsorsak på
            förskrivningen för direktåtkomst för patienten. Jag har efter menprövning enligt offentlighets-
            och sekretesslagen (25 kap. 6 §) eller patientsäkerhetslagen (6 kap. 12 § andra stycket) bedömt
            att det med hänsyn till ändamålet med vården eller behandlingen är av synnerlig vikt att
            uppgiften inte lämnas ut till patienten.
        `,
    },
    [`${NllLockType.SEKRETESS_VH.code}-${NllLockSubject.FORSKRIVNINGSKEDJA.code}`]: {
        value: {
            sparr_legal_ground: NllLockType.SEKRETESS_VH.code,
            sparr_subject_type: NllLockSubject.FORSKRIVNINGSKEDJA.code,
        },
        label: "Spärr för vårdnadshavare, sekretesspärr",
        description: `
            Du som förskrivare begär att E-hälsomyndigheten ska spärra en förskrivning och dess uttag för
            direktåtkomst för barnets vårdnadshavare
        `,
        disclaimer: `
            Du som förskrivare begär att E-hälsomyndigheten ska spärra en förskrivning och dess uttag för
            direktåtkomst för barnets vårdnadshavare.  Det innebär att spärrad info inte visas för barnet,
            vårdnadshavaren eller ombud med fullmakt – till exempel vid e-handel på apotek
        `,
        infoNll: `
            Som förskrivare av läkemedel begär jag att E-hälsomyndigheten ska spärra en förskrivning och dess uttag
            för direktåtkomst för barnets vårdnadshavare. Jag har efter menprövning enligt offentlighets- och
            sekretesslagen (12 kap. 3 §) eller patientsäkerhetslagen (6 kap. 12 §)
            bedömt att barnet kan lida betydande men om vårdnadshavaren tar del av uppgiften.
        `,
        maxAge: 17,
    },
    [`${NllLockType.SEKRETESS_VH.code}-${NllLockSubject.UTTAGPAPPERSRECEPT.code}`]: {
        value: {
            sparr_legal_ground: NllLockType.SEKRETESS_VH.code,
            sparr_subject_type: NllLockSubject.UTTAGPAPPERSRECEPT.code,
        },
        label: "Spärr för vårdnadshavare, sekretesspärr",
        description: `
            Du som förskrivare begär att E-hälsomyndigheten ska spärra samtliga uttag gjorda på pappersrecept för
            direktåtkomst för barnets vårdnadshavare
        `,
        disclaimer: `
            Du som förskrivare begär att E-hälsomyndigheten ska spärra samtliga befintliga och framtida uttag på
            pappersrecept för direktåtkomst för barnets vårdnadshavare.  Det innebär att spärrad info inte visas
            för barnet, vårdnadshavaren eller ombud med fullmakt – till exempel vid e-handel på apotek
        `,
        infoNll: `
            Som förskrivare av läkemedel begär jag att E-hälsomyndigheten ska spärra samtliga uttag gjorda 
            på pappersrecept för direktåtkomst för barnets vårdnadshavare. Jag har efter menprövning enligt 
            offentlighets- och sekretesslagen (12 kap. 3 §) eller patientsäkerhetslagen (6 kap. 12 §) bedömt 
            att barnet kan lida betydande men om vårdnadshavaren tar del av uppgiften.
        `,
        maxAge: 17,
    },
};

export const getLockName = ({ value }: { value: SparrValue | null }) => {
    if (!value) {
        return "";
    }

    if (value.extra) {
        return `${value.sparr_legal_ground}-${value.sparr_subject_type}-${value.extra}`;
    }

    return `${value.sparr_legal_ground}-${value.sparr_subject_type}`;
};

/**
 * Konverterar response från nll-kodverk till options för användning i t.ex KAutocomplete
 */
export const getNllKodverkOptions = ({ data }: { data: Array<NllKodverk> }): NllKodverkOption[] => {
    return data.map(({ code, display, patient_display }) => ({
        value: code,
        label: capitalize({ text: display }),
        patient_display,
    }));
};
